import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMainAxios } from '../utils/util';
import { useAuth } from '../context/AuthContext';

/**
 *  LLM ▸ 커서 요청
 *
 *  문제를 적어 보내면 fdspc 의 cursor-agent 가 코드베이스를 보고 진단한다.
 *
 *  흐름 (AI 채팅과 **다른 기능**이다 — 저쪽은 Ollama 대화창):
 *
 *    화면 POST /api/v1/ailoop/ask   → main.ailoopask (state=0 대기)
 *      → fdsloopsvc 의 asksvc 워커가 집음 (state=1 실행중)
 *      → cursor-agent 가 리포를 읽고 답을 만든다 (수 분)
 *      → 같은 행에 answer 저장 (state=2 완료)
 *    화면은 3초마다 폴링해 답이 오면 보여준다.
 *
 *  왜 웹이 직접 커서를 부르지 않나: 1건에 수 분이 걸려 HTTP 가 못 기다리고,
 *  ASP.NET 스레드에서 외부 프로세스를 띄우면 웹 전체가 느려진다.
 *
 *  설계: docs/design/AILoop-설계-구현계획.md §9 · sql/ailoop/05_ask_table.sql
 */

type Ask = {
  id: number;
  state: number;          // 0 대기 1 실행중 2 완료 -1 실패 -2 가드차단 -9 취소
  askby: string;
  question: string;
  ctx?: string | null;
  lvlgate: number;
  provider?: string | null;
  reqt: string;
  startt?: string | null;
  endt?: string | null;
  answer?: string | null;
  artifact?: string | null;
  costusd?: number | null;
  msg?: string | null;
  anslen?: number | null;
};

const STATE: Record<number, { label: string; color: string; bg: string }> = {
  0: { label: '대기', color: '#92400e', bg: '#fef3c7' },
  1: { label: '실행중', color: '#1d4ed8', bg: '#dbeafe' },
  2: { label: '완료', color: '#065f46', bg: '#d1fae5' },
  [-1]: { label: '실패', color: '#991b1b', bg: '#fee2e2' },
  [-2]: { label: '차단', color: '#7c2d12', bg: '#ffedd5' },
  [-9]: { label: '취소', color: '#374151', bg: '#f3f4f6' },
};

const stateOf = (s: number) => STATE[s] ?? { label: `?${s}`, color: '#374151', bg: '#f3f4f6' };

/** 진행 중인 요청이 있으면 폴링 주기를 짧게 — 없으면 길게(서버 부담을 줄인다). */
const POLL_BUSY_MS = 3000;
const POLL_IDLE_MS = 20000;

const CursorAsk: React.FC = () => {
  const axiosInstance = useMemo(() => getMainAxios(), []);
  const { user } = useAuth();
  const askBy = user?.email || 'anonymous';

  const [question, setQuestion] = useState('');
  const [ctx, setCtx] = useState('');
  const [level, setLevel] = useState(1);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [list, setList] = useState<Ask[]>([]);
  const [sel, setSel] = useState<Ask | null>(null);
  const selIdRef = useRef<number | null>(null);
  selIdRef.current = sel?.id ?? null;

  const fetchList = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/api/v1/ailoop/ask', { params: { limit: 50 } });
      setList(Array.isArray(r.data) ? r.data : []);
    } catch { /* 네트워크 끊김은 다음 주기에 다시 — 화면을 비우지 않는다 */ }
  }, [axiosInstance]);

  const fetchOne = useCallback(async (id: number) => {
    try {
      const r = await axiosInstance.get(`/api/v1/ailoop/ask/${id}`);
      setSel(r.data);
    } catch { /* 위와 같다 */ }
  }, [axiosInstance]);

  // 목록 폴링. 진행 중(0·1)이 있으면 3초, 없으면 20초.
  useEffect(() => {
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      if (!alive) return;
      await fetchList();
      // 열어 둔 항목이 아직 끝나지 않았으면 그것도 같이 새로고침한다.
      const id = selIdRef.current;
      if (id != null) {
        const cur = list.find(x => x.id === id);
        if (!cur || cur.state === 0 || cur.state === 1) await fetchOne(id);
      }
      if (!alive) return;
      const busy = list.some(x => x.state === 0 || x.state === 1);
      timer = setTimeout(tick, busy ? POLL_BUSY_MS : POLL_IDLE_MS);
    };

    tick();
    return () => { alive = false; clearTimeout(timer); };
    // list 를 의존성에 넣으면 매 갱신마다 타이머가 재생성된다 — 의도적으로 뺀다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchList, fetchOne]);

  const submit = async () => {
    const q = question.trim();
    if (!q) { setErr('문제를 적어라'); return; }
    setSending(true); setErr(null);
    try {
      const r = await axiosInstance.post('/api/v1/ailoop/ask', {
        askBy, question: q, ctx: ctx.trim() || null, level,
      });
      setQuestion(''); setCtx('');
      await fetchList();
      if (r.data?.id) await fetchOne(r.data.id);
    } catch (e: any) {
      setErr(e?.response?.data?.error || e?.message || '전송 실패');
    } finally {
      setSending(false);
    }
  };

  const cancel = async (id: number) => {
    try {
      await axiosInstance.post(`/api/v1/ailoop/ask/${id}/cancel`);
      await fetchList();
    } catch (e: any) {
      setErr(e?.response?.data?.error || '취소 실패');
    }
  };

  const fmt = (s?: string | null) => (s ? String(s).replace('T', ' ').slice(0, 19) : '-');

  const card: React.CSSProperties = {
    backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: 8,
    padding: '10px 12px', marginBottom: 10,
  };
  const h3: React.CSSProperties = { margin: 0, color: '#1e40af', fontSize: 14, fontWeight: 600 };
  const lbl: React.CSSProperties = { fontSize: 12, color: '#64748b', minWidth: 54 };

  const busyCnt = list.filter(x => x.state === 0 || x.state === 1).length;

  return (
    <div style={{ padding: 12, boxSizing: 'border-box' }}>

      {/* 입력 */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
          <h3 style={h3}>커서 요청</h3>
          <span style={{ fontSize: 11, color: '#64748b' }}>
            문제를 적으면 fdspc 의 cursor-agent 가 코드를 보고 진단한다. 답까지 보통 1~5분.
          </span>
          {busyCnt > 0 && (
            <span style={{
              marginLeft: 'auto', fontSize: 11, padding: '2px 8px', borderRadius: 4,
              background: '#dbeafe', color: '#1d4ed8', fontWeight: 600,
            }}>
              진행 중 {busyCnt}건
            </span>
          )}
        </div>

        <textarea
          value={question}
          onChange={e => setQuestion(e.target.value)}
          placeholder={'예) 포트폴리오 차트에서 1분 버튼을 눌러도 봉 간격이 안 바뀐다. 왜 그런지 찾아줘.'}
          rows={4}
          style={{
            width: '100%', boxSizing: 'border-box', fontSize: 12, padding: 8,
            border: '1px solid #cbd5e1', borderRadius: 4, resize: 'vertical',
            fontFamily: 'inherit',
          }}
        />

        <details style={{ marginTop: 6 }}>
          <summary style={{ fontSize: 11, color: '#64748b', cursor: 'pointer' }}>
            맥락 추가 (로그·에러 메시지 — 선택)
          </summary>
          <textarea
            value={ctx}
            onChange={e => setCtx(e.target.value)}
            placeholder="에러 로그, 재현 순서, 관련 화면 등"
            rows={5}
            style={{
              width: '100%', boxSizing: 'border-box', fontSize: 11, padding: 8, marginTop: 6,
              border: '1px solid #cbd5e1', borderRadius: 4, resize: 'vertical',
              fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
            }}
          />
        </details>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
          <span style={lbl}>단계</span>
          <select
            value={level}
            onChange={e => setLevel(Number(e.target.value))}
            style={{
              padding: '3px 8px', fontSize: 12, border: '1px solid #93c5fd',
              borderRadius: 4, color: '#1d4ed8', background: '#fff',
            }}
          >
            <option value={1}>1 — 진단만 (원인·확인방법)</option>
            <option value={2}>2 — 패치 초안까지 (적용은 안 함)</option>
          </select>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            레벨 3(자동적용) 이상은 코드로 막혀 있다
          </span>

          <button
            onClick={submit}
            disabled={sending || !question.trim()}
            style={{
              marginLeft: 'auto', padding: '5px 16px', fontSize: 12, fontWeight: 600,
              border: 'none', borderRadius: 4, cursor: sending ? 'default' : 'pointer',
              background: sending || !question.trim() ? '#cbd5e1' : '#2563eb', color: '#fff',
            }}
          >
            {sending ? '보내는 중…' : '커서에 요청'}
          </button>
        </div>

        {err && (
          <div style={{
            marginTop: 8, padding: '6px 10px', fontSize: 12, borderRadius: 4,
            background: '#fee2e2', color: '#991b1b',
          }}>
            {err}
          </div>
        )}
      </div>

      {/* 목록 + 상세 */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        <div style={{ ...card, flex: '1 1 380px', minWidth: 320, marginBottom: 0 }}>
          <h3 style={{ ...h3, marginBottom: 8 }}>요청 내역</h3>
          {list.length === 0 && (
            <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0' }}>아직 요청이 없다.</div>
          )}
          <div style={{ maxHeight: 520, overflowY: 'auto' }}>
            {list.map(a => {
              const st = stateOf(a.state);
              const active = sel?.id === a.id;
              return (
                <div
                  key={a.id}
                  onClick={() => fetchOne(a.id)}
                  style={{
                    padding: '6px 8px', marginBottom: 4, borderRadius: 4, cursor: 'pointer',
                    border: active ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                    background: active ? '#eff6ff' : '#fff',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                      color: st.color, background: st.bg,
                    }}>
                      {st.label}
                    </span>
                    <span style={{ fontSize: 10, color: '#94a3b8' }}>#{a.id}</span>
                    <span style={{ fontSize: 10, color: '#94a3b8' }}>L{a.lvlgate}</span>
                    <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 'auto' }}>
                      {fmt(a.reqt).slice(5, 16)}
                    </span>
                    {a.state === 0 && (
                      <button
                        onClick={e => { e.stopPropagation(); cancel(a.id); }}
                        style={{
                          fontSize: 10, padding: '0 6px', border: '1px solid #cbd5e1',
                          borderRadius: 3, background: '#fff', color: '#64748b', cursor: 'pointer',
                        }}
                      >
                        취소
                      </button>
                    )}
                  </div>
                  <div style={{
                    fontSize: 12, color: '#334155', marginTop: 3,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {a.question}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{ ...card, flex: '2 1 520px', minWidth: 360, marginBottom: 0 }}>
          {!sel && (
            <div style={{ fontSize: 12, color: '#94a3b8' }}>
              왼쪽에서 요청을 고르면 답이 여기 나온다.
            </div>
          )}
          {sel && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <h3 style={h3}>#{sel.id}</h3>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3,
                  color: stateOf(sel.state).color, background: stateOf(sel.state).bg,
                }}>
                  {stateOf(sel.state).label}
                </span>
                <span style={{ fontSize: 11, color: '#64748b' }}>
                  {sel.askby} · 레벨 {sel.lvlgate}
                  {sel.provider ? ` · ${sel.provider}` : ''}
                  {sel.costusd != null ? ` · $${Number(sel.costusd).toFixed(4)}` : ''}
                </span>
                <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>
                  요청 {fmt(sel.reqt)}{sel.endt ? ` · 완료 ${fmt(sel.endt)}` : ''}
                </span>
              </div>

              <div style={{
                fontSize: 12, padding: '6px 8px', borderRadius: 4,
                background: '#f8fafc', border: '1px solid #e2e8f0', marginBottom: 8,
                whiteSpace: 'pre-wrap',
              }}>
                {sel.question}
              </div>

              {(sel.state === 0 || sel.state === 1) && (
                <div style={{ fontSize: 12, color: '#1d4ed8' }}>
                  {sel.state === 0
                    ? '대기 중 — 워커가 곧 집는다.'
                    : '커서가 코드베이스를 보고 있다. 보통 1~5분 걸린다.'}
                </div>
              )}

              {sel.msg && (
                <div style={{
                  fontSize: 12, padding: '6px 8px', borderRadius: 4, marginBottom: 8,
                  background: '#fef3c7', color: '#92400e', whiteSpace: 'pre-wrap',
                }}>
                  {sel.msg}
                </div>
              )}

              {sel.answer && (
                <pre style={{
                  fontSize: 12, lineHeight: 1.5, margin: 0, padding: 10,
                  background: '#0f172a', color: '#e2e8f0', borderRadius: 4,
                  maxHeight: 600, overflow: 'auto', whiteSpace: 'pre-wrap',
                  fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                }}>
                  {sel.answer}
                </pre>
              )}

              {sel.artifact && (
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                  산출물: <code>{sel.artifact}</code>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CursorAsk;
